/**
 * ProcessPreview — Phase 3a
 *
 * A confirmation card shown BEFORE executing a multi-step backend tool chain
 * ([COPILOT_CHAIN]). Lists each step (capability + human description + body
 * summary), shows step dependencies (binding arrows), and Confirm / Cancel.
 *
 * During execution it shows live per-step progress
 * (step 1 ✓ → step 2 running → …). On error in a step, execution stops and the
 * failed step is highlighted.
 *
 * Rendered as a portal (z-index 9998, like the HITLGate). Driven by the
 * 'copilot:chainPreview' CustomEvent dispatched by GrcCopilotSidebar.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowDown,
  Link2,
  AlertTriangle,
  X,
  ListChecks,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  executeChain,
  type BackendToolCall,
  type ToolResult,
  type ChainProgress,
} from './tool-executor'

// ── Event payload ─────────────────────────────────────────────────────────────

export interface ChainPreviewPayload {
  requestId: string
  steps: BackendToolCall[]
  /** Called with the full results array when the chain finishes (or stops). */
  onComplete: (results: ToolResult[]) => void
  /** Called when the user cancels before execution. */
  onCancel: () => void
}

declare global {
  interface WindowEventMap {
    'copilot:chainPreview': CustomEvent<ChainPreviewPayload>
  }
}

// ── Per-step UI status ────────────────────────────────────────────────────────

type StepStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped'

function bodySummary(body?: Record<string, unknown>): string {
  if (!body || Object.keys(body).length === 0) return '—'
  const keys = Object.keys(body)
  const preview = keys
    .slice(0, 3)
    .map((k) => `${k}: ${String(body[k]).slice(0, 24)}`)
    .join(', ')
  return keys.length > 3 ? `${preview}, +${keys.length - 3}` : preview
}

function methodColor(method: string): string {
  const m = method.toUpperCase()
  if (m === 'GET') return 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
  if (m === 'POST') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
  if (m === 'PUT' || m === 'PATCH') return 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
  if (m === 'DELETE') return 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
  return 'bg-muted text-muted-foreground'
}

// ── ProcessPreview component ───────────────────────────────────────────────────

export function ProcessPreview() {
  const [payload, setPayload] = useState<ChainPreviewPayload | null>(null)
  const [statuses, setStatuses] = useState<StepStatus[]>([])
  const [results, setResults] = useState<ToolResult[]>([])
  const [running, setRunning] = useState(false)
  const [finished, setFinished] = useState(false)
  const seenIds = useRef<Set<string>>(new Set())

  // ── Listen for chainPreview events ──────────────────────────────────────────
  useEffect(() => {
    function onPreview(e: CustomEvent<ChainPreviewPayload>) {
      const p = e.detail
      if (seenIds.current.has(p.requestId)) return
      seenIds.current.add(p.requestId)
      setPayload(p)
      setStatuses(p.steps.map(() => 'pending'))
      setResults([])
      setRunning(false)
      setFinished(false)
    }
    window.addEventListener('copilot:chainPreview', onPreview)
    return () => window.removeEventListener('copilot:chainPreview', onPreview)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!payload || running) return
    setRunning(true)

    const onProgress = (prog: ChainProgress) => {
      setStatuses((prev) => {
        const next = [...prev]
        if (prog.phase === 'running') next[prog.stepIndex] = 'running'
        else if (prog.phase === 'done') next[prog.stepIndex] = 'done'
        else if (prog.phase === 'failed') next[prog.stepIndex] = 'failed'
        return next
      })
      if (prog.result) {
        setResults((prev) => {
          const next = [...prev]
          next[prog.stepIndex] = prog.result!
          return next
        })
      }
    }

    const finalResults = await executeChain(payload.steps, onProgress)

    // Mark any step never reached (chain stopped on failure) as skipped.
    setStatuses((prev) =>
      prev.map((s, i) =>
        i >= finalResults.length && s === 'pending' ? 'skipped' : s,
      ),
    )

    setRunning(false)
    setFinished(true)
    payload.onComplete(finalResults)
  }, [payload, running])

  const handleCancel = useCallback(() => {
    if (!payload) return
    if (!running && !finished) payload.onCancel()
    setPayload(null)
  }, [payload, running, finished])

  if (!payload) return null

  const { steps } = payload
  const failedIndex = statuses.findIndex((s) => s === 'failed')

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Aperçu du processus Copilot"
      className="fixed inset-0 z-[9998] flex items-center justify-end p-6 pointer-events-none"
    >
      <div
        className="absolute inset-0 bg-black/10 pointer-events-auto"
        onClick={!running ? handleCancel : undefined}
        aria-hidden="true"
      />

      <div
        className={cn(
          'relative pointer-events-auto w-full max-w-md rounded-xl border shadow-2xl bg-background',
          'animate-in slide-in-from-bottom-4 duration-200 mr-[420px] max-h-[80vh] flex flex-col',
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-4 pt-4 pb-2 shrink-0">
          <div className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-primary shrink-0" />
            <span className="text-sm font-semibold">
              {finished
                ? 'Processus terminé'
                : running
                ? 'Exécution du processus…'
                : `Processus en ${steps.length} étape(s) — confirmer ?`}
            </span>
          </div>
          <button
            onClick={handleCancel}
            disabled={running}
            className="text-muted-foreground hover:text-foreground ml-2 shrink-0 disabled:opacity-40"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Step list */}
        <div className="px-4 pb-2 overflow-y-auto space-y-1">
          {steps.map((step, idx) => {
            const status = statuses[idx] ?? 'pending'
            const result = results[idx]
            const bindsTo = step.bindFrom
              ? parseInt(/^\$(\d+)\./.exec(step.bindFrom.trim())?.[1] ?? '0', 10)
              : null

            return (
              <div key={idx}>
                {/* Binding arrow from a prior step */}
                {bindsTo !== null && bindsTo > 0 && (
                  <div className="flex items-center gap-1 pl-3 text-[10px] text-primary/70">
                    <Link2 className="h-3 w-3" />
                    <span>lie la sortie de l&apos;étape {bindsTo} → {step.bindFrom}</span>
                    <ArrowDown className="h-3 w-3" />
                  </div>
                )}

                <div
                  className={cn(
                    'flex items-start gap-2 rounded-md border px-2.5 py-2',
                    status === 'running' && 'border-primary/60 bg-primary/5',
                    status === 'done' && 'border-emerald-300/60 bg-emerald-50/40 dark:bg-emerald-950/20',
                    status === 'failed' && 'border-red-300/60 bg-red-50/40 dark:bg-red-950/20',
                    status === 'skipped' && 'opacity-50',
                    status === 'pending' && 'border-border',
                  )}
                >
                  {/* Status icon */}
                  <div className="shrink-0 mt-0.5">
                    {status === 'running' && <Loader2 className="h-4 w-4 text-primary animate-spin" />}
                    {status === 'done' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                    {status === 'failed' && <XCircle className="h-4 w-4 text-red-600" />}
                    {(status === 'pending' || status === 'skipped') && (
                      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
                        {idx + 1}
                      </span>
                    )}
                  </div>

                  {/* Step detail */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <Badge
                        variant="secondary"
                        className={cn('text-[10px] h-4 px-1.5 font-mono', methodColor(step.method))}
                      >
                        {(step.method ?? '').toUpperCase()}
                      </Badge>
                      <span className="text-xs font-mono text-muted-foreground truncate">
                        {step.path}
                      </span>
                    </div>
                    {step.description && (
                      <p className="text-xs mt-0.5">{step.description}</p>
                    )}
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5 truncate">
                      <span className="font-mono">{step.capability_code}</span>
                      {step.body && Object.keys(step.body).length > 0 && (
                        <> · {bodySummary(step.body)}</>
                      )}
                    </p>

                    {/* Per-step result */}
                    {result && (
                      <p
                        className={cn(
                          'text-[10px] mt-1',
                          result.ok ? 'text-emerald-600' : 'text-red-600',
                        )}
                      >
                        {result.ok
                          ? `✓ ${result.status} ${
                              result.data?.id ? `· id=${result.data.id}` : ''
                            }`
                          : `✗ ${result.status} · ${result.error ?? 'échec'}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Failure banner */}
        {failedIndex >= 0 && (
          <div className="mx-4 mb-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-700 px-3 py-2 text-xs text-red-700 dark:text-red-300 flex items-center gap-1.5 shrink-0">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            <span>
              Arrêté à l&apos;étape {failedIndex + 1}. Les étapes suivantes n&apos;ont pas été exécutées.
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="border-t px-4 py-3 flex items-center justify-end gap-2 shrink-0">
          {!finished ? (
            <>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleCancel} disabled={running}>
                Annuler
              </Button>
              <Button size="sm" className="h-7 text-xs" onClick={handleConfirm} disabled={running} autoFocus>
                {running ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin mr-1" /> En cours…
                  </>
                ) : (
                  `Exécuter ${steps.length} étape(s)`
                )}
              </Button>
            </>
          ) : (
            <Button size="sm" className="h-7 text-xs" onClick={() => setPayload(null)}>
              Fermer
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default ProcessPreview
